import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiList } from "@/integrations/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ReportShell } from "@/features/reports/ReportShell";
import { KpiCard } from "@/features/reports/KpiCard";
import { Loader2, AlertTriangle } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/reports/breakdown-analysis")({
  component: BreakdownReport,
});



type Row = {
  id: string;
  wo_number: string;
  reported_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  severity: string;
  status: string;
  problem_description: string | null;
  root_cause: string | null;
  action_taken: string | null;
  downtime_minutes: number | null;
  branch_id: string | null;
  department_id: string | null;
  reporting_department_id: string | null;
  asset: { id: string; asset_code: string; name: string; branch_id: string | null; custodian_dept_id: string | null; custodian_dept: { name: string } | null } | null;
  reporting_department: { name: string } | null;
  department: { name: string } | null;
  vendor: { name: string } | null;
  contract: { contract_no: string } | null;
};

function BreakdownReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["rep_breakdown", { ...filters, branchId: effectiveBranchId }],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 1000, type: "Breakdown" };
      if (effectiveBranchId) params.branch_id = effectiveBranchId;
      if (filters.departmentId) params.department = filters.departmentId;
      if (filters.dateFrom) params.reported_at__gte = filters.dateFrom;
      if (filters.dateTo) params.reported_at__lte = filters.dateTo;
      
      const res = await apiList<any>("/api/v1/work-orders/", params);
      const list = res.results || [];

      // Repeat-failure: last 90 days, >=3 breakdowns per asset (independent of filters)
      const since = Date.now() - 90 * 24 * 60 * 60 * 1000;
      const counts = new Map<string, number>();
      for (const r of list) {
        if (!r.asset?.id) continue;
        if (new Date(r.reported_at).getTime() < since) continue;
        counts.set(r.asset.id, (counts.get(r.asset.id) ?? 0) + 1);
      }
      const repeats = [...counts.values()].filter((n) => n >= 3).length;
      return { rows: list, stats: { total: list.length, repeats } };
    },
  });

  const rows = data?.rows ?? [];
  const deptName = (r: Row) =>
    r.reporting_department?.name ?? r.department?.name ?? r.asset?.custodian_dept?.name ?? "—";
  const vendorContract = (r: Row) =>
    [r.vendor?.name, r.contract?.contract_no].filter(Boolean).join(" / ") || "—";

  const exportRows = useMemo(
    () => () =>
      rows.map((r) => ({
        WO: r.wo_number,
        Reported: r.reported_at,
        Closed: r.closed_at ?? "",
        Asset: r.asset?.name ?? "",
        Code: r.asset?.asset_code ?? "",
        Department: deptName(r),
        Severity: r.severity,
        Status: r.status,
        DowntimeMin: r.downtime_minutes ?? "",
        RootCause: r.root_cause ?? "",
        ActionTaken: r.action_taken ?? "",
        VendorContract: vendorContract(r),
        Problem: r.problem_description ?? "",
      })),
    [rows]
  );

  return (
    <ReportShell
      id="R-04"
      title="Breakdown Analysis"
      description="Corrective work orders by asset and department."
      filters={filters}
      onChange={setFilters}
      enabled={["branchId", "departmentId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <KpiCard label="Total Breakdowns" value={isLoading ? "—" : data?.stats.total ?? 0} />
        <KpiCard
          label="Repeat-Failure Assets (≥3 / 90d)"
          value={isLoading ? "—" : data?.stats.repeats ?? 0}
          tone={(data?.stats.repeats ?? 0) > 0 ? "warning" : "default"}
        />
      </div>
      {error ? (
        <Card>
          <CardContent className="flex items-start gap-3 p-4 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Failed to load breakdown data</div>
              <div className="text-xs opacity-80">{(error as Error).message}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="grid place-items-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO #</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Reported</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Downtime (min)</TableHead>
                    <TableHead>Root Cause</TableHead>
                    <TableHead>Action Taken</TableHead>
                    <TableHead>Vendor / Contract</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 200).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.wo_number}</TableCell>
                      <TableCell>{r.asset?.name ?? "—"}</TableCell>
                      <TableCell>{deptName(r)}</TableCell>
                      <TableCell>{r.severity}</TableCell>
                      <TableCell className="text-sm">{r.reported_at.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-sm">{r.status}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.downtime_minutes ?? "—"}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{r.root_cause ?? "—"}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{r.action_taken ?? "—"}</TableCell>
                      <TableCell className="text-sm">{vendorContract(r)}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="py-8 text-center text-sm text-muted-foreground">
                        No breakdowns found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            <div className="border-t p-3 text-xs text-muted-foreground">
              {rows.length > 200
                ? `Showing first 200 of ${rows.length} breakdowns. Export for full list.`
                : `Showing ${rows.length} of ${rows.length} breakdowns.`}
            </div>
          </CardContent>
        </Card>
      )}
    </ReportShell>
  );
}
